/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {KTSVG} from '../../../helpers'
import {updateDevice, deviceDetails, getModelDevices} from '../../../../api/devicesApi'

// --------------------------------------------------
// Tipi
// --------------------------------------------------

type Props = {
  show: boolean
  handleClose: () => void
  recordId: number
}

interface DeviceModel {
  ID_Tipologia_device: number
  Nome: string
}

const modalsRoot = document.getElementById('root-modals') || document.body

/**
 * Modal di aggiornamento visore (nome, seriale, modello).
 * Il campo Modello è un <select> popolato dai record di tipologia_device.
 */
const UpdateGoggle = ({show, handleClose, recordId}: Props) => {
  // --------------------------------------------------
  // STATE
  // --------------------------------------------------
  const [nome, setNome] = useState<string>('')
  const [seriale, setSeriale] = useState<string>('')
  const [modello, setModello] = useState<number | null>(null)

  const [models, setModels] = useState<DeviceModel[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // --------------------------------------------------
  // EFFECT: Carica dati visore + lista modelli al mount del modal
  // --------------------------------------------------
  useEffect(() => {
    if (show && recordId != null) {
      fetchGoggleData(recordId)
    }
  }, [show, recordId])

  /**
   * Recupera: 1) elenco modelli (tipologia_device)
   *           2) dettagli singolo device
   */
  const fetchGoggleData = async (id: number) => {
    setLoading(true)
    try {
      // 1. Lista modelli
      const modelsResponse: DeviceModel[] = await getModelDevices()
      setModels(modelsResponse)

      // 2. Dettagli dispositivo
      const res = await deviceDetails(id)
      // Gestione possibili forme di risposta
      let deviceData: any
      if (res && typeof res === 'object') {
        if ('data' in res) {
          const payload = (res as any).data
          deviceData = Array.isArray(payload) ? payload[0] : payload
        } else if (Array.isArray(res)) {
          deviceData = res[0]
        } else {
          deviceData = res
        }
      }
      // Imposta campi
      setNome(deviceData?.Nome ?? '')
      setSeriale(deviceData?.Numero_seriale ?? '')
      setModello(deviceData?.COD_Tipologia_device ?? null)
    } catch (error) {
      console.error('Errore durante il recupero dati:', error)
    } finally {
      setLoading(false)
    }
  }

  // --------------------------------------------------
  // AGGIORNA VISORE
  // --------------------------------------------------
  const handleUpdateGoggle = async () => {
    if (nome.trim().length === 0) {
      setFormError('Il nome è obbligatorio')
      return
    }
    try {
      await updateDevice(recordId, {nome, seriale, modello})
      // reset campi
      setNome('')
      setSeriale('')
      setModello(null)
      handleClose()
      setShowConfirmModal(true)
    } catch (error) {
      console.error(error)
    }
  }

  const closeConfirmModal = () => setShowConfirmModal(false)

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return createPortal(
    <>
      {/* Modal principale */}
      <Modal
        id='kt_modal_update_delivery_effective'
        tabIndex={-1}
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered mw-900px'
        show={show}
        onHide={handleClose}
      >
        <div className='modal-header'>
          <h2>Modifica visore</h2>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>

        <div className='modal-body'>
          {/* Nome */}
          <label htmlFor='nome' className='form-label required'>Nome</label>
          <input
            type='text'
            id='nome'
            value={nome}
            onChange={(e) => {
              const v = e.target.value
              setNome(v)
              // se l'utente digita → reset dell’errore
              if (v.trim().length > 0) setFormError(null)
            }}
            className='form-control form-control-lg form-control-solid'
          />
          {formError && (
            <div className='text-danger fw-bold mt-2'>{formError}</div>
          )}
          <br />

          {/* Numero seriale */}
          <label htmlFor='seriale' className='form-label'>Numero seriale</label>
          <input
            type='text'
            id='seriale'
            value={seriale}
            onChange={(e) => setSeriale(e.target.value)}
            className='form-control form-control-lg form-control-solid'
          />
          <br />

          {/* Modello (select) */}
          <label htmlFor='modello' className='form-label required'>Modello</label>
          <select
            id='modello'
            className='form-select form-select-lg form-select-solid'
            value={modello ?? ''}
            onChange={(e) => setModello(e.target.value ? Number(e.target.value) : null)}
            disabled={loading}
          >
            <option value='' disabled>Seleziona modello</option>
            {models.map((m) => (
              <option key={m.ID_Tipologia_device} value={m.ID_Tipologia_device}>
                {m.Nome}
              </option>
            ))}
          </select>
          <br />

          <button
            className='btn btn-lg btn-light-primary me-3'
            onClick={handleUpdateGoggle}
            disabled={loading}
          >
            {loading ? 'Attendere...' : 'Invio'}
          </button>
        </div>
      </Modal>

      {/* Modal di conferma */}
      <Modal
        id='kt_modal_confirm_assign'
        tabIndex={-1}
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered mw-900px'
        show={showConfirmModal}
        onHide={closeConfirmModal}
      >
        <div className='modal-header'>
          <h2>Aggiornamento visore</h2>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={closeConfirmModal}>
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>
        <div className='modal-body'>
          <p>L'aggiornamento del visore è andato a buon fine!</p>
        </div>
        <div className='modal-footer'>
          <button className='btn btn-lg btn-light-primary me-3' onClick={closeConfirmModal}>
            Chiudi
          </button>
        </div>
      </Modal>
    </>,
    modalsRoot
  )
}

export {UpdateGoggle}
